<template>
  <main class="resetpw">
    <section class="resetpw__card" aria-labelledby="resetpw-title">
      <div class="resetpw__head">
        <h1 id="resetpw-title" class="resetpw__title">
          비밀번호 재설정
        </h1>
        <p class="resetpw__desc">
          가입한 이메일 확인 후 인증코드를 입력하고 새 비밀번호를 설정하세요.
        </p>
      </div>

      <!-- step indicator -->
      <ol class="resetpw__steps" aria-label="비밀번호 재설정 단계">
        <li :class="['step', { 'is-active': step === 1, 'is-done': step > 1 }]">
          <span class="step__num">1</span>
          <span class="step__label">이메일 입력</span>
        </li>
        <li :class="['step', { 'is-active': step === 2, 'is-done': step > 2 }]">
          <span class="step__num">2</span>
          <span class="step__label">인증 확인</span>
        </li>
        <li :class="['step', { 'is-active': step === 3, 'is-done': step > 3 }]">
          <span class="step__num">3</span>
          <span class="step__label">새 비밀번호</span>
        </li>
      </ol>

      <!-- notice / error -->
      <p v-if="noticeMsg" class="resetpw__notice">
        {{ noticeMsg }}
      </p>
      <p v-if="errorMsg" class="resetpw__error" role="alert">
        {{ errorMsg }}
      </p>

      <!-- step 1 -->
      <div v-if="step === 1" class="resetpw__body">
        <label class="resetpw__label" for="email">이메일</label>
        <div class="resetpw__field">
          <input
            id="email"
            v-model.trim="email"
            type="email"
            autocomplete="email"
            inputmode="email"
            placeholder="가입한 이메일을 입력하세요"
            @keyup.enter="handleRequestCode"
          />
        </div>
        <p class="resetpw__hint">
          입력한 이메일로 인증코드를 보내드립니다.
        </p>

        <div class="resetpw__actions resetpw__actions--single">
          <button
            class="resetpw__btn"
            type="button"
            :disabled="loading"
            @click="handleRequestCode"
          >
            {{ loading ? '처리 중...' : '인증코드 받기' }}
          </button>
        </div>
      </div>

      <!-- step 2 -->
      <div v-if="step === 2" class="resetpw__body">
        <label class="resetpw__label">이메일</label>
        <div class="resetpw__readonly">
          {{ maskedEmail }}
        </div>

        <label class="resetpw__label" for="code">인증코드</label>
        <div class="resetpw__field">
          <input
            id="code"
            v-model.trim="token"
            type="text"
            autocomplete="one-time-code"
            placeholder="이메일로 받은 인증코드를 입력하세요"
            @keyup.enter="handleVerifyCode"
          />
        </div>
        <p class="resetpw__hint">
          메일을 받지 못했다면 스팸함도 확인해주세요.
        </p>

        <div class="resetpw__subactions">
          <!-- <button class="resetpw__linkbtn" type="button" :disabled="loading" @click="handleResendCode">
            인증코드 다시 보내기
          </button> -->
          <button class="resetpw__linkbtn" type="button" :disabled="loading" @click="goStep1">
            이메일 다시 입력
          </button>
        </div>

        <div class="resetpw__actions">
          <button
            class="ghost"
            type="button"
            :disabled="loading"
            @click="goStep1"
          >
            이전
          </button>
          <button
            class="resetpw__btn"
            type="button"
            :disabled="loading"
            @click="handleVerifyCode"
          >
            {{ loading ? '처리 중...' : '인증 확인' }}
          </button>
        </div>
      </div>

      <!-- step 3 -->
      <div v-if="step === 3" class="resetpw__body">
        <label class="resetpw__label" for="new-password">새 비밀번호</label>
        <div class="resetpw__field resetpw__field--password">
          <input
            id="new-password"
            v-model="newPassWord"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            placeholder="새 비밀번호를 입력하세요"
            @keyup.enter="handleConfirmPassword"
          />
          <button class="togglepw" type="button" @click="showPassword = !showPassword">
            <span class="sr-only">비밀번호 표시 전환</span>
            <i class="fa-regular fa-eye" aria-hidden="true"></i>
          </button>
        </div>

        <label class="resetpw__label" for="new-password-confirm">새 비밀번호 확인</label>
        <div class="resetpw__field resetpw__field--password">
          <input
            id="new-password-confirm"
            v-model="newPasswordConfirm"
            :type="showPasswordConfirm ? 'text' : 'password'"
            autocomplete="new-password"
            placeholder="새 비밀번호를 다시 입력하세요"
            @keyup.enter="handleConfirmPassword"
          />
          <button class="togglepw" type="button" @click="showPasswordConfirm = !showPasswordConfirm">
            <span class="sr-only">비밀번호 표시 전환</span>
            <i class="fa-regular fa-eye" aria-hidden="true"></i>
          </button>
        </div>

        <p class="resetpw__hint">
          비밀번호는 6자 이상 입력해주세요.
        </p>

        <div class="resetpw__actions">
          <button
            class="ghost"
            type="button"
            :disabled="loading"
            @click="step = 2"
          >
            이전
          </button>
          <button
            class="resetpw__btn"
            type="button"
            :disabled="loading"
            @click="handleConfirmPassword"
          >
            {{ loading ? '처리 중...' : '비밀번호 변경' }}
          </button>
        </div>
      </div>

      <!-- step 4 -->
      <div v-if="step === 4" class="resetpw__body resetpw__body--done">
        <div class="resetpw__success-icon">✓</div>
        <h2 class="resetpw__done-title">비밀번호 변경 완료</h2>
        <p class="resetpw__done-text">
          비밀번호가 정상적으로 변경되었습니다.<br />
          새 비밀번호로 다시 로그인해주세요.
        </p>

        <div class="resetpw__actions resetpw__actions--single">
          <button class="resetpw__btn" type="button" @click="goLogin">
            로그인으로 이동
          </button>
        </div>
      </div>

      <div v-if="step !== 4" class="resetpw__footer">
        <button class="resetpw__footer-link" type="button" @click="goLogin">
          로그인으로 돌아가기
        </button>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  passwordResetRequest,
  passwordResetVerify,
  passwordResetConfirm
} from '@/api/user'

const router = useRouter()

const step = ref(1)
const loading = ref(false)

const email = ref('')
const token = ref('')
const newPassWord = ref('')
const newPasswordConfirm = ref('')

const showPassword = ref(false)
const showPasswordConfirm = ref(false)

const errorMsg = ref('')
const noticeMsg = ref('')

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const maskedEmail = computed(() => {
  if (!email.value || !email.value.includes('@')) return email.value

  const [id, domain] = email.value.split('@')
  if (!id) return email.value

  if (id.length <= 3) {
    return `${id[0] || ''}${'*'.repeat(Math.max(id.length - 1, 0))}@${domain}`
  }

  return `${id.slice(0, 3)}${'*'.repeat(id.length - 3)}@${domain}`
})

const clearMessages = () => {
  errorMsg.value = ''
  noticeMsg.value = ''
}

const validateEmail = () => {
  if (!email.value) {
    errorMsg.value = '이메일을 입력해주세요.'
    return false
  }

  if (!emailRegex.test(email.value)) {
    errorMsg.value = '올바른 이메일 형식이 아닙니다.'
    return false
  }

  return true
}

const validateToken = () => {
  if (!token.value) {
    errorMsg.value = '인증코드를 입력해주세요.'
    return false
  }

  return true
}

const validatePassword = () => {
  if (!newPassWord.value) {
    errorMsg.value = '새 비밀번호를 입력해주세요.'
    return false
  }

  if (!newPasswordConfirm.value) {
    errorMsg.value = '새 비밀번호 확인을 입력해주세요.'
    return false
  }

  if (newPassWord.value.length < 6) {
    errorMsg.value = '비밀번호는 6자 이상 입력해주세요.'
    return false
  }

  if (newPassWord.value !== newPasswordConfirm.value) {
    errorMsg.value = '비밀번호가 일치하지 않습니다.'
    return false
  }

  return true
}

const goStep1 = () => {
  clearMessages()
  token.value = ''
  step.value = 1
}

const goLogin = () => {
  router.push('/login')
}

const handleRequestCode = async () => {
  clearMessages()

  if (!validateEmail()) return

  loading.value = true

  try {
    const result = await passwordResetRequest({
      email: email.value
    })

    if (result?.result === 'ok') {
      noticeMsg.value = result?.resultMsg || '인증코드를 이메일로 보냈습니다.'
      step.value = 2
    } else {
      errorMsg.value = result?.resultMsg || '인증코드 발송에 실패했습니다.'
    }
  } catch (error) {
    errorMsg.value = '인증코드 발송 중 오류가 발생했습니다.'
  } finally {
    loading.value = false
  }
}

// const handleResendCode = async () => {
//   clearMessages()

//   if (!validateEmail()) return

//   loading.value = true

//   try {
//     const result = await passwordResetRequest({
//       email: email.value
//     })

//     if (result?.result === 'ok') {
//       noticeMsg.value = result?.resultMsg || '인증코드를 다시 보냈습니다.'
//     } else {
//       errorMsg.value = result?.resultMsg || '인증코드 재발송에 실패했습니다.'
//     }
//   } catch (error) {
//     errorMsg.value = '인증코드 재발송 중 오류가 발생했습니다.'
//   } finally {
//     loading.value = false
//   }
// }

const handleVerifyCode = async () => {
  clearMessages()

  if (!validateEmail()) return
  if (!validateToken()) return

  loading.value = true

  try {
    const result = await passwordResetVerify({
      email: email.value,
      token: token.value
    })

    if (result?.result === 'ok') {
      noticeMsg.value = result?.resultMsg || '인증이 완료되었습니다.'
      step.value = 3
    } else {
      errorMsg.value = result?.resultMsg || '인증코드가 올바르지 않습니다.'
    }
  } catch (error) {
    errorMsg.value = '인증 확인 중 오류가 발생했습니다.'
  } finally {
    loading.value = false
  }
}

const handleConfirmPassword = async () => {
  clearMessages()

  if (!validateEmail()) return
  if (!validateToken()) return
  if (!validatePassword()) return

  loading.value = true

  try {
    const result = await passwordResetConfirm({
      email: email.value,
      token: token.value,
      newPassWord: newPassWord.value,
      newPasswordConfirm: newPasswordConfirm.value
    })

    if (result?.result === 'ok') {
      noticeMsg.value = ''
      step.value = 4
    } else {
      errorMsg.value = result?.resultMsg || '비밀번호 변경에 실패했습니다.'
    }
  } catch (error) {
    errorMsg.value = '비밀번호 변경 중 오류가 발생했습니다.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.resetpw {
  font-family: system-ui, -apple-system, "Segoe UI", "Noto Sans KR",
    "Apple SD Gothic Neo", "Malgun Gothic", Arial, sans-serif;
  min-height: 80vh;
  display: grid;
  place-items: center;
  /* padding: 48px 16px; */
  background: #f8fafc;
}

.resetpw__card {
  width: 100%;
  max-width: 580px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 44px 36px;
  box-shadow: 0 10px 30px rgba(17, 24, 39, 0.06);
}

.resetpw__head {
  margin-bottom: 28px;
}

.resetpw__title {
  margin: 0 0 10px;
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.2px;
  color: #0f172a;
}

.resetpw__desc {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #475569;
}

.resetpw__steps {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 0;
  margin: 0 0 22px;
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  color: #64748b;
}

.step__num {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e2e8f0;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
}

.step__label {
  font-size: 0.9rem;
  font-weight: 700;
  word-break: keep-all;
}

.step.is-active {
  border-color: #a5b4fc;
  background: #eef2ff;
  color: #1e1b4b;
}

.step.is-active .step__num {
  background: #1d2088;
  color: #fff;
}

.step.is-done {
  border-color: #c7d2fe;
  background: #f8faff;
  color: #1e293b;
}

.step.is-done .step__num {
  background: #1d2088;
  color: #fff;
}

.resetpw__notice,
.resetpw__error {
  margin: 0 0 16px;
  font-size: 0.95rem;
  border-radius: 10px;
  padding: 10px 12px;
}

.resetpw__notice {
  color: #334155;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
}

.resetpw__error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.resetpw__body {
  margin-top: 8px;
}

.resetpw__body--done {
  text-align: center;
  padding-top: 14px;
}

.resetpw__success-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-size: 1.7rem;
  font-weight: 800;
  color: #fff;
  background: #1d2088;
}

.resetpw__done-title {
  margin: 0 0 10px;
  font-size: 1.35rem;
  font-weight: 800;
  color: #0f172a;
}

.resetpw__done-text {
  margin: 0 0 24px;
  color: #475569;
  line-height: 1.7;
}

.resetpw__label {
  display: block;
  margin: 14px 0 8px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #0f172a;
}

.resetpw__field {
  position: relative;
}

.resetpw__field input {
  width: 98%;
  height: 58px;
  padding: 0 0 0 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 17px;
  color: #0f172a;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.resetpw__field input::placeholder {
  color: #94a3b8;
}

.resetpw__field input:focus {
  outline: none;
  border-color: #a5b4fc;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
}

.resetpw__field--password input {
  /* padding-right: 54px; */
}

.togglepw {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.togglepw:hover {
  background: #f8fafc;
}

.resetpw__readonly {
  height: 58px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  font-size: 16px;
  font-weight: 600;
}

.resetpw__hint {
  margin: 8px 0 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #64748b;
}

.resetpw__subactions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.resetpw__linkbtn,
.resetpw__footer-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #1d2088;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
}

.resetpw__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 28px;
}

.resetpw__actions--single {
  grid-template-columns: 1fr;
}

.resetpw__btn,
.ghost {
  height: 58px;
  font-size: 17px;
  border-radius: 14px;
  cursor: pointer;
}

.resetpw__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  color: #fff;
  background: #1d2088;
  font-weight: 700;
  transition: background 0.15s ease;
}

.resetpw__btn:hover {
  background: #220e52;
}

.resetpw__btn:disabled,
.ghost:disabled,
.resetpw__linkbtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-weight: 700;
  color: #0f172a;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.ghost:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.resetpw__footer {
  margin-top: 20px;
  text-align: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 640px) {
  .resetpw__card {
    padding: 28px 18px;
  }

  .resetpw__steps {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .resetpw__actions {
    grid-template-columns: 1fr;
  }
}
</style>